import ConfirmModal from "@/src/components/action-modals/confirm-modal";
import ShareModal from "@/src/components/action-modals/share-modal";
import { usePermissions } from "@/src/hooks/use-permissions";
import ContentRenderer from "@/src/components/text-editor/content-renderer";
import AttachmentViewer, {
  Attachment,
} from "@/src/components/ui-kit/attachment-viewer";
import BackNavigationButton from "@/src/components/ui-kit/back-navigation";
import DropDown from "@/src/components/ui-kit/drop-down";
import BlocksAvatar from "@/src/components/ui-kit/blocks-avatar";
import { useAuth } from "@/src/contexts/auth.context";
import { Category } from "@/src/models/category.model";
import {
  BlocksApiArticle,
  BlocksApiAttachment,
} from "@/src/models/wiki.models";
import { useGetCategoriesQuery } from "@/src/services/api/category/category.hooks";
import {
  resolveAuthorName,
  useToggleWikiSaved,
  useWikiArticle,
  useWikiDeleteArticle,
  useWikiSaveState,
} from "@/src/services/wiki/wiki.hooks";
import { useTheme } from "@/src/styles/theme-provider";
import { router, useLocalSearchParams } from "expo-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ActivityIndicator,
  Image,
  Platform,
  ScrollView,
  Text,
  View,
} from "react-native";

const isWeb = Platform.OS === "web";

type WikiArticleDetailProps = Readonly<{
  article?: BlocksApiArticle;
  onBack?: () => void;
}>;

function ArticleTag({ label }: { label: string }) {
  const { primaryPalette } = useTheme();
  return (
    <View className="flex-row items-center gap-1 px-2 py-1 rounded-full bg-primary-2">
      <Text
        className="text-xs font-medium"
        style={{ color: primaryPalette.default }}
      >
        {label}
      </Text>
    </View>
  );
}

export default function WikiArticleDetail({
  article: articleProp,
  onBack,
}: WikiArticleDetailProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { canUpdate, canDelete } = usePermissions();
  const userId = user?.itemId ?? "";
  const { primaryPalette, resolvedScheme } = useTheme();

  const onyxColor = resolvedScheme === "dark" ? "#D7DADC" : "#22272a";
  const ash3Color = resolvedScheme === "dark" ? "#2d3540" : "#DFE5EC";
  const chalkColor = resolvedScheme === "dark" ? "#141a1f" : "#FFFFFF";

  const { isSaved } = useWikiSaveState();
  const { mutate: toggleSave } = useToggleWikiSaved(userId);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);

  const { articleId } = useLocalSearchParams<{ articleId?: string }>();
  const {
    data: fetchedArticle,
    isLoading,
    isError,
  } = useWikiArticle(articleId, !articleProp);
  const article = articleProp ?? fetchedArticle;

  const { data: categories } = useGetCategoriesQuery({
    pageNo: 1,
    search: "",
    tag: null,
    pageSize: 100,
  });

  const categoryMap = new Map<string, string>(
    (categories?.items ?? []).map((c: Category) => [c.ItemId, c.categoryName]),
  );

  const { mutate: deleteArticle } = useWikiDeleteArticle({
    onSuccess: () => {
      setShowDeleteConfirm(false);
      if (onBack) onBack();
      else router.back();
    },
  });

  if (!articleProp && isLoading) {
    return (
      <View className="flex-1 items-center justify-center">
        <ActivityIndicator size="large" />
      </View>
    );
  }

  if (!articleProp && (isError || !article)) {
    return (
      <View className="flex-1 items-center justify-center">
        <Text className="text-ash-1">{t("LABEL_ARTICLE_NOT_FOUND")}</Text>
      </View>
    );
  }

  if (!article) return null;

  const authorName = resolveAuthorName(article);
  const formattedDate = article.CreatedDate
    ? new Date(article.CreatedDate).toLocaleDateString("en-US", {
        month: "short",
        day: "2-digit",
        year: "numeric",
      })
    : "";

  const attachments: Attachment[] = (article.attachmentUrls ?? []).map(
    (att: BlocksApiAttachment) => ({
      url: att.url,
      type: att.mimeType?.startsWith("image/") ? "image" : "file",
    }),
  );

  const resolvedCategoryNames: string[] = article.categoryIds
    ? (Array.isArray(article.categoryIds)
        ? article.categoryIds
        : [article.categoryIds]
      ).map((id) => categoryMap.get(id) ?? id)
    : [];

  const handleBack = onBack ?? (() => router.back());

  const handleEdit = () => {
    router.push({
      pathname: "/(tabs)/wiki/wiki-creation",
      params: {
        draftId: article.ItemId,
        Title: article.Title,
        Content: article.Content,
        categoryId: article.categoryIds ?? "",
        coverImageUrl: article.coverImageUrl ?? "",
        attachments: JSON.stringify(
          (article.attachmentUrls ?? []).map((att) => ({
            id: att.id,
            name: att.name,
            size: att.size,
            url: att.url,
            mimeType: att.mimeType,
          })),
        ),
      },
    });
  };

  const dropdownItems = [
    {
      label: isSaved(article.ItemId) ? t("BTN_UNSAVE") : t("BTN_SAVE"),
      onPress: () => toggleSave(article.ItemId),
    },
    ...(canUpdate("wiki")
      ? [{ label: t("BTN_UPDATE"), onPress: handleEdit }]
      : []),
    { label: t("LABEL_SHARE"), onPress: () => setShowShareModal(true) },
    ...(canDelete("wiki")
      ? [
          {
            label: t("BTN_DELETE"),
            onPress: () => setShowDeleteConfirm(true),
            destructive: true,
          },
        ]
      : []),
  ];

  return (
    <View className={`flex-1 gap-4 ${isWeb ? "flex-row" : "flex-col"}`}>
      <View className="flex-col flex-1 gap-4 bg-chalk">
        {isWeb && (
          <View className="flex-row items-center bg-chalk border px-4 py-6 border-ash-3 rounded-2xl">
            <BackNavigationButton
              label={t("LABEL_BACK")}
              iconSize={16}
              iconClassName="text-onyx"
              labelClassName="text-onyx"
              containerStyle={{ marginBottom: 0 }}
              onPress={handleBack}
            />
            <Text className="absolute inset-x-0 pointer-events-none text-base font-semibold text-center text-onyx">
              {t("LABEL_ARTICLE_DETAIL")}
            </Text>
            <View />
          </View>
        )}

        <ScrollView
          className="flex-1 bg-chalk p-4 gap-4 rounded-2xl md:border md:border-ash-3"
          showsVerticalScrollIndicator={false}
          contentContainerStyle={{ paddingBottom: 40 }}
        >
          {!isWeb && (
            <BackNavigationButton
              iconClassName="text-onyx"
              onPress={handleBack}
            />
          )}

          <View className="flex-row items-start justify-between gap-4">
            <View className="flex-1 gap-2">
              <Text className="text-2xl font-bold text-onyx">
                {article.Title}
              </Text>

              {resolvedCategoryNames.length > 0 && (
                <View className="flex-row flex-wrap gap-1">
                  {resolvedCategoryNames.map((name) => (
                    <ArticleTag key={name} label={name} />
                  ))}
                </View>
              )}
            </View>
            <DropDown items={dropdownItems} />
          </View>

          <View className="flex-row items-center mt-6 gap-2">
            <BlocksAvatar
              userId={article.CreatedBy}
              fallbackImageUrl={article.authorSnapshot?.profileImageUrl}
              size={28}
            />
            <Text className="text-sm font-medium text-onyx">{authorName}</Text>
            <Text className="text-xs text-ash-1">{formattedDate}</Text>
          </View>

          {article.coverImageUrl && (
            <View className="rounded-2xl overflow-hidden mt-4">
              <Image
                source={{ uri: article.coverImageUrl }}
                style={{ width: "100%", height: isWeb ? 300 : 200 }}
                resizeMode="cover"
              />
            </View>
          )}

          <View className="mt-4 min-h-[100px]">
            <ContentRenderer
              key={resolvedScheme}
              dom={{
                matchContents: true,
                scrollEnabled: false,
                style: { backgroundColor: chalkColor },
              }}
              content={article.Content ?? ""}
              primaryColor={primaryPalette.default}
              textColor={onyxColor}
              borderColor={ash3Color}
              containerBg={chalkColor}
            />
          </View>

          {attachments.length > 0 && (
            <View className="border-t pt-4 gap-3 mt-4 border-ash-3">
              <Text className="text-sm font-semibold text-onyx">
                {t("LABEL_ATTACHMENT")}
              </Text>
              <AttachmentViewer attachments={attachments} />
            </View>
          )}
        </ScrollView>
      </View>

      <ConfirmModal
        visible={showDeleteConfirm}
        title="BTN_DELETE"
        description="LABEL_DESC_DELETE_WIKI"
        confirmText={t("BTN_DELETE")}
        cancelText={t("BTN_CANCEL")}
        onConfirm={() => deleteArticle(article.ItemId)}
        onCancel={() => setShowDeleteConfirm(false)}
      />
      <ShareModal
        visible={showShareModal}
        onClose={() => setShowShareModal(false)}
        resourceId={article.ItemId}
        resourcePath="wiki"
        title={article.Title}
      />
    </View>
  );
}
